import { Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { App, HeaderMainNav, NavigationGroup, NavigationLink } from '@/graphql/__generated/sdk';
import { ContentTile as TContentTile } from '@/types/generated';

import { menuDrawerStyles } from './Header.styles';
import LocationCardActions from './LocationCardActions';
import MenuList, { MenuListItemType } from './MenuList';
import ExploreCarousel from './ExploreCarousel/ExploreCarousel';
import { OfficeDetails } from './OfficeDetails';
import CustomTypography from './UI/Typography/CustomTypography';
import CustomButton from './UI/Button/CustomButton';
import { ExtendedFacility } from './Header';

interface MenuDrawerProps {
	selectedOffice?: ExtendedFacility;
	currentMenuLevel: 'main' | 'submenu' | 'thirdlevel';
	selectedMainItem: HeaderMainNav;
	selectedSubItem: NavigationGroup;
	headerData: App['header'];
	phoneNumber: string;
	homeUrl: string;
	onChangeLocationClick: () => void;
	onBackToMain: () => void;
	onBackToSubmenu: () => void;
	onMainItemClick: (item: HeaderMainNav) => void;
	onSubItemClick: (group: NavigationGroup) => void;
	onNavigationClick: (destinationUrl: string, linkText: string) => void;
	onTileClick: (tile: TContentTile, href: string) => void;
	onScheduleClick: (scheduleUrl: string, linkText: string) => void;
}

export function MenuDrawer({
	selectedOffice,
	currentMenuLevel,
	selectedMainItem,
	selectedSubItem,
	headerData,
	phoneNumber: _phoneNumber,
	homeUrl: _homeUrl,
	onChangeLocationClick,
	onBackToMain,
	onBackToSubmenu,
	onMainItemClick,
	onSubItemClick,
	onNavigationClick,
	onTileClick,
	onScheduleClick,
}: MenuDrawerProps) {
	return (
		<>
			<Box id="selected-office-container" sx={menuDrawerStyles.selectedOfficeContainer}>
				<Box display="flex" flexDirection="column" gap="0.25rem" alignItems="flex-start">
					<CustomTypography variant="bodyLBold">
						{selectedOffice?.name || 'No Office Selected'}
					</CustomTypography>
					<CustomButton
						variantType="tertiary"
						size="large"
						onClick={onChangeLocationClick}
						sx={menuDrawerStyles.changeLocation}
					>
						Change Location
					</CustomButton>
				</Box>
				<OfficeDetails
					facility={selectedOffice as ExtendedFacility}
					distanceInMiles={selectedOffice?.distanceInMiles}
					showPhoneAndStatus={true}
				/>
				<LocationCardActions
					selectedOffice={selectedOffice as ExtendedFacility}
					onScheduleClick={onScheduleClick}
				/>
			</Box>

			<Box id="drawer-links-container" sx={menuDrawerStyles.drawerLinksContainer}>
				<Box sx={menuDrawerStyles.drawerLinksGroupHeader} id="drawer-links-group-header">
					{(currentMenuLevel === 'submenu' || currentMenuLevel === 'thirdlevel') && (
						<ArrowBack
							fontSize="small"
							onClick={currentMenuLevel === 'submenu' ? onBackToMain : onBackToSubmenu}
							sx={{ marginRight: '0.5rem', fontSize: '1.5rem', cursor: 'pointer', fill: '#656565' }}
						/>
					)}

					<CustomTypography variant="bodySSemiBold">
						{currentMenuLevel === 'main' && 'Dental Services'}
						{currentMenuLevel === 'submenu' && selectedMainItem?.headerMainNavLink?.linkText}
						{currentMenuLevel === 'thirdlevel' && selectedSubItem?.groupLink?.linkText}
					</CustomTypography>
				</Box>
				{/* Main Menu Level */}
				{currentMenuLevel === 'main' && headerData?.navigationMenu?.headerMainNavLinksCollection?.items && (
					<MenuList
						items={headerData.navigationMenu.headerMainNavLinksCollection.items as NavigationLink[]}
						getText={(item: MenuListItemType) => {
							const mainItem = item as HeaderMainNav;
							return mainItem?.headerMainNavLink?.linkText ?? '';
						}}
						getHref={(item: MenuListItemType) => {
							const mainItem = item as HeaderMainNav;
							const hasSubNav = mainItem.headerSubNavMenu?.headerSubNavGroupsCollection?.items?.length;
							if (hasSubNav) {
								return undefined;
							}
							return (
								mainItem.headerMainNavLink?.pageData?.path ??
								mainItem.headerMainNavLink?.externalUrl ??
								undefined
							);
						}}
						hasNextLevel={(item: MenuListItemType) => {
							const mainItem = item as HeaderMainNav;
							return (mainItem.headerSubNavMenu?.headerSubNavGroupsCollection?.items?.length ?? 0) > 0;
						}}
						onItemClick={(item: MenuListItemType) => onMainItemClick(item as HeaderMainNav)}
						onLinkClick={onNavigationClick}
					/>
				)}{' '}
				{/* Submenu Level */}
				{currentMenuLevel === 'submenu' && selectedMainItem && (
					<MenuList
						items={
							selectedMainItem?.headerSubNavMenu?.headerSubNavGroupsCollection?.items as NavigationLink[]
						}
						getText={(group: MenuListItemType) => {
							const navGroup = group as NavigationGroup;
							return navGroup.groupLink?.linkText ?? '';
						}}
						getHref={(group: MenuListItemType) => {
							const navGroup = group as NavigationGroup;
							const hasLinks = navGroup.linksCollection?.items?.length;
							if (hasLinks) {
								return undefined;
							}
							return navGroup?.groupLink?.pageData?.path ?? navGroup?.groupLink?.externalUrl ?? undefined;
						}}
						hasNextLevel={(group: MenuListItemType) => {
							const navGroup = group as NavigationGroup;
							return (navGroup.linksCollection?.items?.length ?? 0) > 0;
						}}
						onItemClick={(item: MenuListItemType) => onSubItemClick(item as NavigationGroup)}
						onLinkClick={onNavigationClick}
					/>
				)}
				{/* Third Level - Individual Links */}
				{currentMenuLevel === 'thirdlevel' && selectedSubItem && (
					<MenuList
						items={selectedSubItem?.linksCollection?.items as NavigationLink[]}
						getText={(link: MenuListItemType) => {
							const navLink = link as NavigationLink;
							return navLink.linkText ?? '';
						}}
						getHref={(link: MenuListItemType) => {
							const navLink = link as NavigationLink;
							return navLink?.pageData?.path ?? navLink?.externalUrl ?? '';
						}}
						onLinkClick={onNavigationClick}
					/>
				)}
			</Box>

			{/* Explore Carousel - rendered as separate card on gray background */}
			{currentMenuLevel === 'submenu' &&
				selectedMainItem?.headerSubNavMenu?.contentTilesCollection?.items &&
				selectedMainItem.headerSubNavMenu.contentTilesCollection.items.length > 0 && (
					<ExploreCarousel
						tiles={selectedMainItem?.headerSubNavMenu?.contentTilesCollection?.items}
						onTileClick={onTileClick}
					/>
				)}
		</>
	);
}
